import "./App.css";
import "../custom.scss";
import React, { useEffect, useState } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReportViewer } from "pages/ReportViewer/ReportViewer";
import { LogIn } from "pages/login/LogIn";
import { ForgottenPassword } from "pages/login/ForgottenPassword";
import { ForgottenPwdSent } from "pages/login/ForgottenPwdSent";
import { LoginSupport } from "pages/login/LoginSupport";
import { NotFound } from "pages/NotFound/NotFound";
import { CoveragePage } from "pages/Coverage/Coverage";
import { AdvertProvider } from "pages/Ads/Adverts/AdvertProvider";
import { LogOut } from "pages/login/Logout";
import { AnalystProfile } from "pages/AnalystProfile/AnalystProfile";
import { AnalystDirectory } from "pages/AnalystDirectory/AnalystDirectory";
import { LeaveSiteWarning } from "pages/Ads/Adverts/LeaveSiteWarning";
import { Faq } from "pages/Faq/Faq";
import { Disclosures } from "pages/Disclosures/Disclosures";
import { AdvertPreview } from "pages/Ads/Adverts/preview/AdvertPreview";
import { LandingPagePreview } from "pages/LandingPage/preview/LandingPagePreview";
import { ToastNotificationsService } from "pages/ReadingList/notifications/ToastNotificationsService";
import { ReadingList } from "pages/ReadingList/ReadingList";
import { LandingPage } from "pages/LandingPage/LandingPage";
import { FaqPreview } from "pages/Faq/FaqPreview";
import { AllAdverts } from "pages/Ads/Adverts/preview/AllAdverts";
import { LazyEconomicData } from "pages/EconomicData/LazyEconomicData";
import { SessionExpired } from "pages/login/SessionExpired";
import { DisclosuresStatement } from "pages/Disclosures/parts/DisclosuresStatement";
import { Header } from "pages/redesign/header/Header";
import { PublicFaqPage } from "pages/login/PublicFaqPage";

import Redesign from "pages/redesign/Redesign";
import { MagicLink } from "pages/login/MagicLink";
import { MagicLinkVerify } from "pages/login/MagicLinkVerify";
import { VideoPlayer } from "pages/videoplayer/VideoPlayer";
import { Events } from "pages/Events/Events";
import { useLocation } from "react-router";
import { ToastContainer } from "lib/toast/ToastContainer";
import { Disclaimers } from "./Disclaimers/Disclaimers";
import { features } from "./features";
import { ScreenHeight } from "../lib/ScreenHeight";
import { PreferencesProvider } from "../components/Preferences/provider/PreferencesProvider";
import { ChangePassword } from "../pages/login/ChangePassword";
import { ScrollToTop } from "../lib/ScrollToTop";
import { TermsOfService } from "./Disclaimers/TermsOfService";
import { Footer } from "./footer/Footer";
import { ScrollToTopButton } from "./footer/parts/ScrollToTopButton";
import { ChatbotIcon } from "../chatbot/ChatbotIcon";
// eslint-disable-next-line import/namespace
import { ChatbotPanel } from "../chatbot/ChatbotPanel";
import { isIntranetPath } from "../lib/routing";
import { auditChatbotClick } from "../lib/analytics/auditEvents";
import useIsMobile from "../lib/hooks/useIsMobile";
import { NairaTermsOfService } from "./Disclaimers/NairaTermsOfService";
import { getNairaDisclaimer } from "./services/nairaService";
import { DOMICILE } from "../redux/slices/api/initialisePreferences";

function App() {
  const [panelWidth, setPanelWidth] = useState(435);
  const [chatOpen, setChatOpen] = useState(false);
  const [initialChatTab, setInitialChatTab] = useState(0);
  const [initialPublicationId, setInitialPublicationId] = useState(null);
  const [isChatPopOut, setIsChatPopOut] = useState(false);
  const [chatFullscreen, setChatFullscreen] = useState(false);
  const [showNairaTermsOfService, setShowNairaTermsOfService] = useState(false);
  const [isLoadingDisclaimer, setIsLoadingDisclaimer] = useState(false);
  const [nairaDisclaimerJson, setNairaDisclaimerJson] = useState();
  const [initialQuery, setInitialQuery] = useState(null);
  // Common style for shrinking
  const shrinkStyle = {
    width: chatOpen && !isChatPopOut ? `calc(99vw - ${panelWidth}px)` : "100%",
    transition: "width 0.3s cubic-bezier(.4,0,.2,1)"
  };
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [showChatbotIcon, setShowChatbotIcon] = useState(false);
  const domicile = useSelector((state) => state.preferences[DOMICILE])[0];
  const isJapanUser = domicile === "ja";

  useEffect(() => {
    (async () => {
      if (
          features.chatbot.showChatIcon &&
          isIntranetPath() &&
          !isMobile &&
          !pathname.includes("preview") &&
          !chatOpen &&
          !isChatPopOut &&
          (!isJapanUser || features.chatbot.jpUsers)
      ) {
        setShowChatbotIcon(true);
      } else {
        setShowChatbotIcon(false);
      }
    })();
  }, [pathname, chatOpen, isMobile, isChatPopOut]);

  const handleChatBotClick = async () => {
    try {
      setIsLoadingDisclaimer(true);
      auditChatbotClick();
      const response = await getNairaDisclaimer();
      if (response?.showNairaDisclaimer) {
        setShowNairaTermsOfService(true);
        setNairaDisclaimerJson(response.nairaDisclaimerJson);
      } else {
        setChatOpen(true);
      }
    } catch (error) {
      console.error("Error fetching disclaimer");
    } finally {
      setIsLoadingDisclaimer(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const pubId = e.detail?.publicationId ?? null;
      const tab = e.detail?.tab ?? 0;
      const query = e.detail?.query ?? null;
      if (chatOpen) {
        // Panel already mounted — tell the controller directly
        window.dispatchEvent(new CustomEvent("naira-load-summary", { detail: { publicationId: pubId } }));
        if (query) {
          window.dispatchEvent(
              new CustomEvent("naira-send-query", { detail: { query } })
          );
        }
      } else {
        setInitialChatTab(tab);
        setInitialPublicationId(pubId);
        if (query) setInitialQuery(query);
        handleChatBotClick();
      }
    };
    window.addEventListener("open-naira-chat", handler);
    return () => window.removeEventListener("open-naira-chat", handler);
  }, [chatOpen]);

  return (
    <div style={{ display: "flex", width: "100%", height: "100vh" }}>
      <main style={{ flex: 1, transition: "margin-right 0.2s" }}>
        <PreferencesProvider>
          <Switch>
            <Route path="/ext/publication/:pid/:resourceType" component={ReportViewer} />

            {/* All other routes with Header/Footer */}
            <Route>
              <div style={{ ...shrinkStyle, display: "inline-block", verticalAlign: "top" }}>
                <Header
                  style={shrinkStyle}
                  chatOpen={chatOpen && !isChatPopOut}
                  panelWidth={panelWidth}
                  chatFullscreen={chatFullscreen}
                />
                <ScreenHeight>
                  <TermsOfService />
                  <AdvertProvider />
                  <ScrollToTop />
                  <Switch>
                    {/* Public paths */}
                    <Route path="/public/login" component={LogIn} />
                    <Route path="/public/forgotten-password/" component={ForgottenPassword} />
                    <Route path="/public/forgotten-password-sent/" component={ForgottenPwdSent} />
                    <Route path="/public/magic-link/" component={MagicLink} />
                    <Route path="/public/magic-link-verify/" component={MagicLinkVerify} />
                    <Route path="/public/login-support/" component={LoginSupport} />
                    <Route path="/public/change-password/" component={ChangePassword} />
                    <Route path="/public/session_expired" component={SessionExpired} />
                    <Route path="/public/Disclaimers/:disclaimer" component={Disclaimers} />
                    <Route path="/public/Disclosures/Statements" component={DisclosuresStatement} />
                    <Route path="/public/Disclosures" component={Disclosures} />
                    <Route path="/public/videoplayer/" component={VideoPlayer} />
                    <Route path="/public/Analyst_Directory/" component={AnalystDirectory} />
                    <Route path="/public/brochures" component={LandingPage} />
                    <Route path="/public/FAQ" component={PublicFaqPage} />
                    {/* Directus preview */}
                    {features.preview.faq && <Route path="/FAQ/preview/:id" component={FaqPreview} />}
                    {features.preview.disclaimer && (
                      <Route path="/Disclaimers/preview/:disclaimer" component={Disclaimers} />
                    )}
                    {features.preview.banners && <Route path="/advert/preview/all" component={AllAdverts} />}
                    {features.preview.banners && <Route path="/advert/preview/:id" component={AdvertPreview} />}
                    {features.preview.banners && <Route path="/advert2/preview/:id" component={AdvertPreview} />}
                    {features.preview.landingPages && (
                      <Route path="/Landing_Page/preview/:topic" component={LandingPagePreview} />
                    )}
                    {RedesignRedirectionRules()}
                    <Route path="/My_Reading_List" component={ReadingList} />
                    <Route path="/FAQ" component={Faq} />
                    <Route path="/Sign_Out" component={LogOut} />
                    <Route path="/Equity_Coverage" component={CoveragePage} />
                    <Route path="/Analyst_Directory" component={AnalystDirectory} />
                    <Route path="/Analyst" component={AnalystProfile} />
                    <Route path="/GotoWarning" component={LeaveSiteWarning} />
                    <Route path="/Disclaimers/:disclaimer" component={Disclaimers} />
                    {/* feature-flag routes */}
                    {features.disclosures && <Route path="/Disclosures/Statements" component={DisclosuresStatement} />}
                    {features.disclosures && <Route path="/Disclosures" component={Disclosures} />}
                    {features.economicForecast && <Route path="/Economic_Data/" component={LazyEconomicData} />}
                    {features.bannerLandingPages && <Route path="/Topics/:topic" component={LandingPage} />}
                    {/* extension routes */}
                    {features.events && <Route path="/Events" component={Events} />}
                    {/* legacy URLs */}
                    {features.economicForecast && (
                      <Route path="/Topics/Economic_Forecast" component={LazyEconomicData} />
                    )}
                    {features.economicForecast && <Route path="/Economic_Forecast" component={LazyEconomicData} />}
                    <Route component={NotFound} />
                  </Switch>
                </ScreenHeight>
                <ToastNotificationsService />
                <Footer />
              </div>
            </Route>
          </Switch>
        </PreferencesProvider>
      </main>
      <ScrollToTopButton
        chatbotIcon={
          showChatbotIcon ? (
            <div className="chatbot-container">
              {isLoadingDisclaimer && <div className="chatbot-spinner spinner-border text-primary spinner-grow-sm" />}
              <ChatbotIcon onClick={handleChatBotClick} />
            </div>
          ) : null
        }
      />
      {showNairaTermsOfService && (
        <NairaTermsOfService
          open={showNairaTermsOfService}
          setOpen={setShowNairaTermsOfService}
          openChat={() => {
            setChatOpen(true);
          }}
          disclaimerJson={nairaDisclaimerJson}
        />
      )}
      {chatOpen && (
        <ChatbotPanel
          panelWidth={panelWidth}
          setPanelWidth={setPanelWidth}
          onClose={() => {
            setChatOpen(false);
            setIsChatPopOut(false);
            setInitialQuery(null);
          }}
          style={{ width: panelWidth, minWidth: 280, maxWidth: 600, height: "100vh" }}
          onPopStateChange={(popout) => setIsChatPopOut(popout)}
          onFullscreenChange={(fs) => setChatFullscreen(fs)}
          initialTab={initialChatTab}
          publicationId={initialPublicationId}
          initialQuery={initialQuery}
        />
      )}
      <ToastContainer />
    </div>
  );
}

export default App;

const RedesignRedirectionRules = () => [
  <Route key={0} path="/latest-research/Equity" component={() => <Redirect to="/Equity" />} />,
  <Route key={1} path="/latest-research/Economics" component={() => <Redirect to="/Economics" />} />,
  <Route key={2} path="/latest-research/FX" component={() => <Redirect to="/FX" />} />,
  <Route key={3} path="/latest-research/Quants" component={() => <Redirect to="/Quants" />} />,
  <Route key={4} path="/latest-research/Quants Focus" component={() => <Redirect to="/Quants" />} />,
  <Route key={5} path="/latest-research/Rates" component={() => <Redirect to="/Rates" />} />,
  <Route key={6} path="/latest-research/Rates Focus" component={() => <Redirect to="/Rates" />} />,
  <Route key={7} path="/latest-research/SP__Credit" component={() => <Redirect to="/SP__Credit" />} />,
  <Route key={8} path="/latest-research/SP__Credit Focus" component={() => <Redirect to="/SP__Credit" />} />,
  <Route key={9} path="/latest-research/ESG" component={() => <Redirect to="/Home?reportTypes.en=ESG" />} />,
  <Route key={10} path="/latest-research/" component={() => <Redirect to="/Home" />} />,
  <Route key={11} path="/" exact component={() => <Redirect to="/Home" />} />,
  <Route key={12} path="/Credit" component={() => <Redirect to="/SP__Credit" />} />,
  <Route
    key={13}
    path={redirectToHomeWithParams}
    component={({ location }) => {
      const to = `/Home${location.search}`;
      return <Redirect to={to} />;
    }}
  />,

  <Route key={14} path={redesignPaths} component={Redesign} />
];

const redirectToHomeWithParams = ["/Search_Results", "/Most_Popular", "/My_Research"];
const redesignPaths = ["/Home", "/Economics", "/Equity", "/FX", "/Rates", "/SP__Credit", "/Macro Strategy", "/Quants"];
